import React, { useMemo } from 'react';
import { Offer, ProductInterface } from '../../services/exports/Interfaces';
import useMenu from '../../hooks/menu/useMenu';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import useModalRouter from '../../modules/mobile/common/hooks/useModalRouter';
import { CompiledProduct } from '../../providers/MenuProvider';
import { Modals } from '../../modules/mobile/exports/Enums';
import classnames from 'classnames';
import { swipeableCssClass } from '../../modules/mobile/exports/Constants';
import CustomCarousel, { Item } from '../../components-library/carousel/CustomCarousel';
import OfferProduct from './OfferProduct';
import useScreenType from '../../hooks/utility/useScreenType';

type Props = {
  offer: Offer;
  products: number[];
  title: string;
  description: string;
  hasAddBtn?: boolean;
};

const OfferProducts = (props: Props) => {
  const { compileProduct } = useMenu();
  const { toggleMenuItemModal, toggleOfferModal } = useAppMethods();
  const closeOfferModal = () => toggleOfferModal({ openModal: false });
  const { isMobileApp } = useMobileApp();
  const { isDesktop } = useScreenType();
  const { openModal, closeModal } = useModalRouter();
  const products: CompiledProduct[] = useMemo(
    () => props.products?.map(compileProduct)?.filter(Boolean),
    [props.products, compileProduct],
  );
  const onCloseMenuItemModal = () => toggleOfferModal({ openModal: true, offer: props.offer });
  const toggleMobileAppProduct = (product: ProductInterface) => {
    return openModal({
      id: Modals.MenuItem,
      presentation: {
        initialBreakpoint: 1,
        breakpoints: [0, 1],
        handle: false,
        cssClass: classnames(swipeableCssClass),
      },
      props: {
        product,
        onCloseModal: closeModal,
        onBack: () => {
          openModal({
            id: Modals.OfferModal,
            props: {
              offer: props.offer,
            },
          });
        },
        showProductAddedToast: true,
      },
    });
  };

  const toggleProduct = (product: ProductInterface) => {
    closeOfferModal();

    if (!isMobileApp) {
      return toggleMenuItemModal({
        openModal: true,
        product: product,
        onBack: onCloseMenuItemModal,
      });
    }

    toggleMobileAppProduct(product);
  };

  const items: Item[] = useMemo(
    () =>
      products?.map((product) => ({
        data: product,
        render: () => (
          <OfferProduct
            product={product}
            key={product.id}
            {...(props.hasAddBtn && { onClick: () => toggleProduct(product) })}
          />
        ),
      })),
    [products, props.hasAddBtn],
  );
  const itemHeight = props.hasAddBtn ? 285 : 185;
  const itemsPerSlide = isDesktop ? 4 : undefined;

  return (
    <>
      <CustomCarousel
        itemsPerSlide={itemsPerSlide}
        items={items}
        arrowsPosition="top"
        arrowsClassName="lg:mr-lg mr-md"
        itemWidth={185}
        itemHeight={itemHeight}
        title={<h3 className="text-md">{props.title}</h3>}
        {...(props.description && { description: <p className="text-brand-text-secondary">{props.description}</p> })}
      />
    </>
  );
};

export default OfferProducts;
